import { i18n } from 'boot/i18n';
import { network } from 'boot/network';
import { get, isEmpty } from 'lodash';
import { notifyWarning } from 'src/composables/interactions';
import ClientsDB from 'src/fb/clients-db';
import SessionsDB from 'src/fb/sessions-db';
import UsersClientsDB from 'src/fb/usersClients-db';
import { auth as useAuthStore } from 'src/store/auth';
import { nativeAppUpdates as useNativeAppUpdatesStore } from 'src/store/nativeAppUpdates';
import { network as useNetworkStore } from 'src/store/network';
import { createRouter, createWebHashHistory, createWebHistory } from 'vue-router';

import { useNativeAppUpdates } from '../composables/native-app-updates';
import { userRoles } from '../utils/AppUtils';
import routes from './routes';

// We aren't using the function from `useAuth` composable,
// because useStore hook returns undefined in the context of the router
const userHasPrivilege = (privilege) => {
  const authStore = useAuthStore();

  const userRole = authStore.userDetails?.role || null;

  const lockedPrivilege = authStore.lockedPrivileges[userRole]?.[privilege];
  const defaultPrivilege = authStore.defaultPrivileges[userRole]?.[privilege];

  if (lockedPrivilege) {
    return defaultPrivilege;
  }

  return authStore.userPrivileges[privilege] ?? defaultPrivilege;
};

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default () => {
  const createHistory = process.env.VUE_ROUTER_MODE === 'history'
    ? createWebHistory
    : createWebHashHistory;

  const Router = createRouter({
    scrollBehavior: (to, from) => {
      if (to.meta.preserveViewOnParamChange && to.name === from.name) {
        return false;
      }
      return { left: 0, top: 0 };
    },
    routes,

    // Leave this as is and make changes in quasar.config.js instead!
    // quasar.config.js -> build -> vueRouterMode
    // quasar.config.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE),
  });

  Router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();
    const networkStore = useNetworkStore();

    if (network.offlineMode && to.name === 'Encryption') {
      return next();
    }

    const selectedRegion = !isEmpty(localStorage.getItem('selected_region'));

    if (to.name === 'region') {
      return next();
    }

    // @todo: sometimes user is logged in but there wasn't enough time for userDetails to be fetched
    const isLoggedIn = !isEmpty(authStore.userDetails);
    const hasNoClientsOrClinic = !authStore.userDetails.admin
      && (!get(authStore, ['connectedClinic', 'id'], null))
      && (get(authStore, ['userDetails', 'associatedClients'], []).length === 0);
    const hasNoBilling = !authStore.userDetails.admin
      && get(authStore, ['connectedClinic', 'id'], false)
      && (get(authStore.billing, 'status', null) === null);

    if (to.meta.requiresRegion && !selectedRegion && !to.query.region && !to.query.email) {
      return next({ name: 'region', query: { redirect: to.fullPath } });
    }
    // non public pages should only be visible to users logged in
    if (!to.meta.public && !isLoggedIn) {
      return next({
        name: 'login',
        query: { ...to.query, redirect: to.fullPath },
      });
    }

    // Prevent access if e-mail is not verified
    if (!to.meta.public
      && (!to.meta.acceptUnverified)
      && (!authStore.emailVerified)) {
      return next({
        path: '/signup/verify-email',
      });
    }

    // Ensure user is signed up to MFA if MFA is required
    if (
      !network.offlineMode
      && !to.meta.public
      && (!to.meta.acceptNoMFA)
      && (authStore.userDetails.requireMFA)
      && (!authStore.userDetails.enrolledMFA)
      && (authStore.emailVerified)
    ) {
      return next({
        path: '/signup/enable-mfa',
      });
    }

    // No clients or clinic attached? Onboard the user
    if (
      isLoggedIn
      && hasNoClientsOrClinic
      && (!to.meta.allowNoClientsOrClinic)
      && to.name !== 'Sign up new clinic' // fixes route guard trying to redirect route to itself
    ) {
      return next({
        name: 'Sign up new clinic',
      });
    }

    // User is a clinic - but there is no billing info
    if (isLoggedIn && hasNoBilling && (!to.meta.allowNoBilling)) {
      return next({
        path: '/signup/terms',
      });
    }

    if (to.meta.admin && !authStore.userDetails.admin) {
      return next({
        path: '/',
      });
    }

    // Check if we need to change client
    if (to.name !== 'login') {
      const resetClient = to.query?.resetClient;

      if (resetClient) {
        await authStore.changeCurrentClient({});
      } else {
        let clientId = to.query?.client;

        if (!clientId) {
          const sessionStorageClient = sessionStorage.getItem('selectedClient');

          if (sessionStorageClient && isEmpty(authStore.currentClient)) {
            clientId = sessionStorageClient;
          }
        }

        if (clientId) {
          try {
            const clientsDb = new ClientsDB();
            const client = await clientsDb.read(clientId);

            if ((client?.type === 'staff' && client?.status === 'inactive') || client?.isDeleted) {
              await authStore.changeCurrentClient({});
            } else {
              await authStore.changeCurrentClient(client);
            }
          } catch (e) {
            console.error('Error loading a client', e);
          }
        }
      }
    }

    // Check if requires a client to be selected
    if (to.meta.requiresClient && isEmpty(authStore.currentClient)) {
      return next({
        path: '/',
      });
    }

    const isInOfflineMode = networkStore.offlineMode;

    if (isInOfflineMode) {
      const isOfflinePage = to.meta.offlineModePage;

      if (!isOfflinePage) {
        notifyWarning(i18n.t('common.errors.offlineForbiddenPage'));
        return next({
          path: '/',
        });
      }
    }

    const userRole = authStore.userDetails.role;
    const adminBypass = to.meta.adminBypass && authStore.userIsAdmin;

    // If it is explicitly public or there is no userRoles it is public
    const isPublic = to.meta.public || !to.meta?.roles?.length;
    const isAllowed = isPublic || adminBypass
      || userRole === userRoles.MANAGER || to.meta?.roles?.includes(userRole);

    let hasPrivileges = true;

    if (to.meta.privileges?.length && !adminBypass) {
      hasPrivileges = to.meta.privileges.every((privilege) => userHasPrivilege(privilege));
    }

    const hasRequiredAddOn = !to.meta.requiresAddOn
      || authStore.userIsAdmin
      || authStore.hasClientAddOn(to.meta.requiresAddOn);

    if (!isAllowed || !hasPrivileges || !hasRequiredAddOn) {
      notifyWarning(i18n.t('common.errors.forbiddenPage'));
      return next({
        path: '/',
      });
    }

    // Check if an active subscription is required
    if (to.meta.requiresActiveSubscription) {
      const isSubscriptionInactive = authStore.isSubscriptionInactive || false;
      const userIsManager = (get(authStore, ['userDetails', 'role']) === userRoles.MANAGER);
      if (isSubscriptionInactive) {
        const redirRouteName = userIsManager ? 'Settings - Subscription' : 'Subscription Expired';
        return next({ name: redirRouteName });
      }
    }
    // The billing add-on is bought separately from the subscription, so a paid-up clinic without it
    // must not reach a billing screen by typing its URL. Hiding the nav item is presentation.
    if (to.meta.requiresRevenueCycle && !authStore.isRevenueCycleAvailable) {
      notifyWarning(i18n.t('common.errors.forbiddenPage'));
      return next({
        path: '/',
      });
    }

    // Check if a client license is required and not present
    if (to.meta.requiresClientLicense) {
      const isSubscriptionInactive = authStore.isSubscriptionInactive || false;
      const clientIsInactive = get(authStore.currentClient, 'status') !== 'active';
      if (isSubscriptionInactive || clientIsInactive) {
        return next({ name: 'No Client License' });
      }
    }

    // Ensures session access, redirects if unauthorized, and switches client if needed
    if (to.name === 'session-data-collection') {
      const sessionId = to.params?.sessionId || null;
      if (sessionId) {
        try {
          const sessionsDb = new SessionsDB();
          const session = await sessionsDb.read(sessionId);

          if (!session) {
            notifyWarning(i18n.t('sessions.permissions.sessionNotFound'));
            return next({
              path: '/',
            });
          }

          const usersClientsDb = new UsersClientsDB();
          const isSameClinic = session.clinic === authStore.connectedClinic?.id;
          const hasClientAccess = authStore.isManagerOrAdministrator
            || Boolean(await usersClientsDb.read(`${authStore.userDetails.userId}_${session.client}`));

          if (!isSameClinic || !hasClientAccess) {
            notifyWarning(i18n.t('sessions.permissions.clientAccessDenied'));
            return next({
              path: '/',
            });
          }

          const clientMismatch = session?.client !== authStore.currentClient?.id;
          if (clientMismatch) {
            const clientsDb = new ClientsDB();
            const client = await clientsDb.read(session?.client);

            if (!client || client?.status === 'inactive') {
              notifyWarning(i18n.t('sessions.permissions.clientNoLongerActive'));
              return next({
                path: '/',
              });
            }
            await authStore.changeCurrentClient(client);
          }
        } catch (e) {
          notifyWarning(i18n.t('common.errors.generic'));
          console.error('Error ensuring session access', e);
          return next({
            path: '/',
          });
        }
      }
    }
    next();
  });

  Router.afterEach((to, from) => {
    const authStore = useAuthStore();
    const nativeAppUpdatesStore = useNativeAppUpdatesStore();

    if (['login', 'signup', 'Sign up new clinic'].includes(from.name)) {
      authStore.changeLoaderState(false);
    }

    const { showAppUpdateBanner, shouldShowAppUpdateBanner, updateApp } = useNativeAppUpdates();

    if (shouldShowAppUpdateBanner() && !to.path.includes('/run/')) {
      const appUpdateInfo = get(nativeAppUpdatesStore, 'appUpdateInfo', {});

      showAppUpdateBanner(() => updateApp(appUpdateInfo?.appStore, appUpdateInfo?.type));
    }
  });

  return Router;
};
